import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signalService } from '../services';
import LoadingSpinner from './LoadingSpinner';
import { initialsFor } from './SignalInvitees';
import usePageScrollLock from '../utils/usePageScrollLock';

/**
 * Everyone a signal went to — the "+N" tap-through.
 *
 * Bottom sheet on mobile, centred modal from `sm` up, matching AddAccountModal's
 * `items-end … sm:items-center` pattern rather than inventing a second one.
 *
 * The list is fetched rather than read off the card: the card carries only the
 * handful of faces it draws, and a truncated list is exactly what someone
 * tapping "see everyone" is trying to get past.
 */
export default function InviteeListSheet({ eventId, invitedCount, onClose }) {
  const navigate = useNavigate();
  const [people, setPeople] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await signalService.getInvitees(eventId);
      if (cancelled) return;
      setPeople(r.success ? (r.data || []) : []);
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  // Escape closes, and the body stops scrolling behind the sheet.
  usePageScrollLock(true);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const open = (p) => {
    onClose();
    navigate(p.handle ? `/${p.handle}` : `/users/${p.userId}`);
  };

  const inCount = (people || []).filter((p) => p.state === 'accepted').length;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Who's invited"
      onClick={onClose}
      className="fixed inset-0 safe-inset z-[200] flex items-end justify-center bg-black/60 sm:items-center"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[80vh] w-full flex-col border-2 border-black bg-white sm:max-w-md"
      >
        <div className="flex items-center justify-between border-b-2 border-black px-4 py-3">
          <span className="font-mono text-[12px] font-bold uppercase tracking-[0.14em] text-black">
            Invited{typeof invitedCount === 'number' ? ` · ${invitedCount}` : ''}
            {inCount > 0 ? ` · ${inCount} in` : ''}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center border-2 border-black bg-white transition-colors hover:bg-black hover:text-white focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
          >
            <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
              <path d="M4 4l12 12M16 4L4 16" />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {people === null ? (
            <div className="p-8"><LoadingSpinner label="Loading…" /></div>
          ) : people.length === 0 ? (
            <div className="p-6 text-center font-mono text-[11px] uppercase tracking-[0.1em] text-hand-muted">
              No one yet.
            </div>
          ) : (
            people.map((p) => (
              <button
                key={p.userId}
                type="button"
                onClick={() => open(p)}
                className="flex w-full items-center gap-3 border-b-2 border-black px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-hand-canvas focus:outline-none focus:ring-2 focus:ring-inset focus:ring-black"
              >
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden border-2 border-black bg-hand-lavender font-mono text-[12px] font-bold text-black">
                  {p.avatarUrl
                    ? <img src={p.avatarUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                    : initialsFor(p.displayName)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] leading-tight text-black">{p.displayName}</span>
                  <span className="block truncate font-mono text-[10px] uppercase tracking-[0.08em] text-hand-muted">
                    {[p.handle ? `@${p.handle}` : '', (p.location || '').toUpperCase()].filter(Boolean).join(' · ')}
                  </span>
                </span>
                {p.state === 'accepted' && (
                  <span className="flex-shrink-0 border-2 border-black bg-glitch-green px-2 py-[3px] font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-black">
                    In
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
